
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ContractType, ContractDirection } from "@/lib/types";
import { toast } from "sonner";

export function useContractTypes() {
  const queryClient = useQueryClient();
  
  // Type for database record
  interface ContractTypeRecord {
    id: string;
    name: string;
    description?: string;
    auto_renews: boolean;
    renewal_interval?: number;
    renewal_unit?: string;
    direction?: string;
    created_at: string;
    updated_at: string;
    user_id: string;
  }
  
  // Get all contract types
  const getContractTypes = async (): Promise<ContractType[]> => {
    const { data, error } = await supabase
      .from("contract_types")
      .select("*")
      .order("name");
      
    if (error) {
      toast.error(`Error fetching contract types: ${error.message}`);
      throw error;
    }
    
    return (data || []).map((item: ContractTypeRecord) => ({
      id: item.id,
      name: item.name,
      description: item.description,
      auto_renews: item.auto_renews,
      renewal_interval: item.renewal_interval,
      renewal_unit: item.renewal_unit as any,
      direction: item.direction as ContractDirection,
      created_at: item.created_at,
      updated_at: item.updated_at
    }));
  };
  
  // Get contract type usage count
  const getContractTypeUsage = async (id: string): Promise<{ count: number }> => {
    const { data, error, count } = await supabase
      .from("contracts")
      .select("id", { count: 'exact' })
      .eq("contract_type_id", id);
      
    if (error) {
      toast.error(`Error fetching contract type usage: ${error.message}`);
      throw error;
    }
    
    return { count: count || 0 };
  };
  
  // Get a single contract type by ID
  const getContractTypeById = async (id: string): Promise<ContractType> => {
    const { data, error } = await supabase
      .from("contract_types")
      .select("*")
      .eq("id", id)
      .single();
      
    if (error) {
      toast.error(`Error fetching contract type: ${error.message}`);
      throw error;
    }
    
    return {
      id: data.id,
      name: data.name,
      description: data.description,
      auto_renews: data.auto_renews,
      renewal_interval: data.renewal_interval,
      renewal_unit: data.renewal_unit as any,
      direction: data.direction as ContractDirection,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  };
  
  // Create a contract type
  const createContractType = async (contractType: Omit<ContractType, "id">): Promise<ContractType> => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      throw new Error("User not authenticated");
    }
    
    // Create a database record with correct shape
    const recordToInsert = {
      name: contractType.name,
      description: contractType.description,
      auto_renews: contractType.auto_renews,
      renewal_interval: contractType.renewal_interval,
      renewal_unit: contractType.renewal_unit,
      direction: contractType.direction,
      created_at: contractType.created_at || new Date().toISOString(),
      updated_at: contractType.updated_at || new Date().toISOString(),
      user_id: userId
    };
    
    const { data, error } = await supabase
      .from("contract_types")
      .insert([recordToInsert])
      .select()
      .single();
      
    if (error) {
      toast.error(`Error creating contract type: ${error.message}`);
      throw error;
    }
    
    toast.success("Contract type created");
    
    return {
      id: data.id,
      name: data.name,
      description: data.description,
      auto_renews: data.auto_renews,
      renewal_interval: data.renewal_interval,
      renewal_unit: data.renewal_unit as any,
      direction: data.direction as ContractDirection,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  };
  
  // Update a contract type
  const updateContractType = async (contractType: ContractType): Promise<ContractType> => {
    // Create a database record with correct shape
    const recordToUpdate = {
      name: contractType.name,
      description: contractType.description,
      auto_renews: contractType.auto_renews,
      renewal_interval: contractType.renewal_interval,
      renewal_unit: contractType.renewal_unit,
      direction: contractType.direction,
      updated_at: new Date().toISOString()
    };
    
    const { data, error } = await supabase
      .from("contract_types")
      .update(recordToUpdate)
      .eq("id", contractType.id)
      .select()
      .single();
      
    if (error) {
      toast.error(`Error updating contract type: ${error.message}`);
      throw error;
    }
    
    toast.success("Contract type updated");
    
    return {
      id: data.id,
      name: data.name,
      description: data.description,
      auto_renews: data.auto_renews,
      renewal_interval: data.renewal_interval,
      renewal_unit: data.renewal_unit as any,
      direction: data.direction as ContractDirection,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  };
  
  // Delete a contract type
  const deleteContractType = async (id: string): Promise<void> => {
    const { error } = await supabase
      .from("contract_types")
      .delete()
      .eq("id", id);
      
    if (error) {
      toast.error(`Error deleting contract type: ${error.message}`);
      throw error;
    }
    
    toast.success("Contract type deleted");
  };
  
  // Reassign contracts from one type to another
  const reassignContractType = async (fromId: string, toId: string): Promise<void> => {
    const { error } = await supabase
      .from("contracts")
      .update({ contract_type_id: toId })
      .eq("contract_type_id", fromId);
      
    if (error) {
      toast.error(`Error reassigning contracts: ${error.message}`);
      throw error;
    }
    
    toast.success("Contracts reassigned");
  };
  
  // Define queries and mutations
  const useContractTypesQuery = () => useQuery({
    queryKey: ["contract-types"],
    queryFn: getContractTypes
  });
  
  const useContractTypeQuery = (id: string) => useQuery({
    queryKey: ["contract-types", id],
    queryFn: () => getContractTypeById(id),
    enabled: !!id
  });

  const useContractTypeUsageQuery = (id: string) => useQuery({
    queryKey: ["contract-types", id, "usage"],
    queryFn: () => getContractTypeUsage(id),
    enabled: !!id
  });
  
  const useCreateContractTypeMutation = () => useMutation({
    mutationFn: createContractType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-types"] });
    }
  });
  
  const useUpdateContractTypeMutation = () => useMutation({
    mutationFn: updateContractType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-types"] });
    }
  });
  
  const useDeleteContractTypeMutation = () => useMutation({
    mutationFn: deleteContractType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-types"] });
    }
  });
  
  const useReassignContractTypeMutation = () => useMutation({
    mutationFn: ({ fromId, toId }: { fromId: string, toId: string }) => 
      reassignContractType(fromId, toId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
    }
  });
  
  return {
    useContractTypesQuery,
    useContractTypeQuery,
    useContractTypeUsageQuery,
    useCreateContractTypeMutation,
    useUpdateContractTypeMutation,
    useDeleteContractTypeMutation,
    useReassignContractTypeMutation
  };
}
